// ══════════════════════════════════════════════════════════════════════════
//  BUSCAR CON CRITERIO: un solo motor para toda la app (2026-09-18)
//
//  El dueño, al buscar "proyecto de taludes" y ver presupuestos que no lo
//  dicen: "no entiendo cómo se están indexando las búsquedas cuando algo tan
//  preciso no presenta resultados, no está pero sigue mostrando lo parecido";
//  y con "cabo real" salían los de Cabo San Lucas porque un concepto decía
//  "realizar". Antes cada palabra valía como pedazo de texto en cualquier parte
//  del registro, y la lista no distinguía lo que coincidía de lo que se parecía.
//
//  Ahora:
//   · Una palabra coincide donde EMPIEZA una palabra ("real" encuentra "real" y
//     "reales", no "irreal"); dentro de otra palabra solo cuenta como parecido.
//   · Las palabras vacías ("de", "la", "y"...) no se exigen solas, pero sí
//     cuentan dentro de la frase.
//   · Tres niveles, en este orden:
//       0  la frase entera aparece tal cual en un campo;
//       1  todas las palabras, completas (o en plural), aparecen juntas en un
//          mismo campo (un nombre, un concepto, una nota), en cualquier orden;
//       2  parecido: las palabras están, pero repartidas o dentro de otras.
//   · Entre comillas ("cabo real") solo vale la frase exacta.
//   · La lista dice la verdad con las MISMAS palabras en toda la app
//     (textosBusqueda): "Coinciden con «X»", "Con todas las palabras",
//     "Parecidos" y, si nada la dice, "Nada coincide con «X»".
//   · Lo escrito casi igual (2026-10-30), SOLO cuando nada coincide de esas
//     tres maneras: ver "Lo escrito casi igual", abajo.
//
//  Puro, sin React; con vitest. utils.coincide y utils.plegar salen de aquí.
// ══════════════════════════════════════════════════════════════════════════

// Pliega para comparar: minúsculas y sin diacríticos ("José" = "jose").
const DIACRITICOS = /[̀-ͯ]/g;
export const plegar = (s) => String(s == null ? '' : s).toLowerCase().normalize('NFD').replace(DIACRITICOS, '');

// Palabras que no se exigen solas: casi todo documento las lleva y exigirlas no
// separa nada. Dentro de la frase sí cuentan ("proyecto de taludes").
const VACIAS = new Set(['de', 'del', 'la', 'las', 'el', 'los', 'lo', 'y', 'e', 'o', 'u', 'a', 'al', 'en',
  'con', 'por', 'para', 'un', 'una', 'unos', 'unas', 'que', 'se', 'su', 'sus']);

// La puntuación pegada a los bordes ("jose," "tules.") no es parte del dato;
// dentro del término se respeta ("14/08/2026", "f-26249", "$53,673.20", "15%").
const BORDES = /^[^\p{L}\p{N}$]+|[^\p{L}\p{N}%]+$/gu;
const COMILLAS = /^\s*["“”«„'](.*?)["“”»']?\s*$/s;

// La consulta como se entiende: `frase` (todas las palabras, en orden),
// `terminos` (las que se exigen), `exacta` (venía entre comillas) y `texto` (lo
// tecleado, para decirlo de vuelta en la lista).
export function consultaDe(q) {
  const crudo = String(q == null ? '' : q);
  const m = COMILLAS.exec(crudo);
  const exacta = !!m && /^\s*["“”«„']/.test(crudo) && crudo.trim().length > 1;
  const dentro = exacta ? m[1] : crudo;
  const palabras = plegar(dentro).split(/\s+/).map((t) => t.replace(BORDES, '')).filter(Boolean);
  const llenas = palabras.filter((t) => !VACIAS.has(t));
  return {
    texto: dentro.trim(),
    frase: palabras.join(' '),
    terminos: llenas.length ? llenas : palabras,
    exacta,
    vacia: palabras.length === 0,
  };
}

const LETRA = /[\p{L}\p{N}]/u;
// ¿`termino` aparece en `texto` (ambos plegados) donde empieza una palabra?
export function alInicioDePalabra(texto, termino) {
  if (!termino) return true;
  let i = texto.indexOf(termino);
  while (i >= 0) {
    if (i === 0 || !LETRA.test(texto[i - 1])) return true;
    i = texto.indexOf(termino, i + 1);
  }
  return false;
}

// ¿`termino` aparece como palabra COMPLETA (o su plural: "proyecto" encuentra
// "proyectos", "taludes" también "talud" no)? "real" no es "realizar".
export function palabraCompleta(texto, termino) {
  if (!termino) return true;
  let i = texto.indexOf(termino);
  while (i >= 0) {
    if (i === 0 || !LETRA.test(texto[i - 1])) {
      const resto = texto.slice(i + termino.length, i + termino.length + 3);
      const fin = (k) => k >= resto.length || !LETRA.test(resto[k]);
      if (fin(0) || (resto[0] === 's' && fin(1)) || (resto.startsWith('es') && fin(2))) return true;
    }
    i = texto.indexOf(termino, i + 1);
  }
  return false;
}

// La frase aparece tal cual, empezando donde empieza una palabra. Los espacios
// del campo se toman como uno ("cabo  real" también dice "cabo real").
const diceLaFrase = (campo, frase) => alInicioDePalabra(campo.replace(/\s+/g, ' '), frase);

// El nivel de un registro para una consulta: 0 dice la frase, 1 tiene todas las
// palabras juntas en un campo, 2 se parece, null no coincide. `campos` ya
// plegados (se pliegan una vez al indexar, no en cada tecleo).
export function nivelDe(consulta, campos) {
  if (!consulta || consulta.vacia) return 0;
  const { frase, terminos, exacta } = consulta;
  if (campos.some((c) => diceLaFrase(c, frase))) return 0;
  if (exacta) return null;
  // Juntas: cada palabra COMPLETA (o su plural) en un mismo campo. La frase
  // acepta la última palabra a medio escribir; aquí no, o "cabo real" juntaría
  // "Cabo San Lucas, realizar sondeos".
  if (terminos.length > 1 && campos.some((c) => terminos.every((t) => palabraCompleta(c, t)))) return 1;
  const todo = campos.join('\n');
  return terminos.every((t) => todo.includes(t)) ? 2 : null;
}

// Atajo para quien tiene la consulta cruda y los campos sin plegar.
export const nivel = (q, ...campos) => nivelDe(consultaDe(q), campos.map(plegar));

// ¿Coincide, en cualquier nivel? Lo que usan los filtros de la app (antes,
// utils.coincide). Sin consulta, todo coincide. Es estricto: no conoce lo
// escrito casi igual, que se decide sobre la lista entera (nivelesDe).
export const coincide = (q, ...campos) => nivel(q, ...campos) != null;

// ── Lo escrito casi igual (2026-10-30) ─────────────────────────────────────
//  El dueño, al buscar "gpeasa" en Presupuestos: "no sale nada y debería
//  salir" (la empresa está registrada GCPEASA; con "rangel", el destinatario,
//  sí salía). Los tres niveles exigen cada palabra tal cual, y una letra de
//  menos la dejaba fuera. Ahora, SOLO si nada de la lista coincide de esas tres
//  maneras, se busca lo escrito casi igual (nivel 3; la lista lo rotula
//  "Parecidos" bajo "Nada coincide con «X»"):
//   · cada palabra está tal cual o escrita casi igual donde EMPIEZA una
//     palabra del registro (la palabra del registro puede seguir: se está
//     escribiendo), y al menos una casi igual;
//   · casi igual = una letra cambiada, de más, de menos o dos volteadas, desde
//     5 letras; dos desde 9. Una palabra corta o con dígitos (folios,
//     importes, fechas) se exige tal cual: un número distinto es otro documento;
//   · entre comillas no hay casi igual.
//  Se decide sobre la LISTA (nada coincidió), nunca registro por registro: por
//  eso coincide y nivelDe siguen estrictos y los menús y filtros no se vuelven
//  difusos.
export const NIVEL_CASI_IGUAL = 3;

const SOLO_LETRAS = /^\p{L}+$/u;
const PALABRA = /[\p{L}\p{N}]+/gu;
const CON_DIGITO = /\d/;

// Las letras que puede errar una palabra de la consulta (0: se exige tal cual).
export const letrasDeHolgura = (termino) => {
  const t = String(termino || '');
  if (t.length < 5 || !SOLO_LETRAS.test(t)) return 0;
  return t.length < 9 ? 1 : 2;
};

// Las palabras de un texto ya plegado.
export const palabrasDe = (texto) => String(texto || '').match(PALABRA) || [];

// Las palabras de cada registro (un Set por registro) y el vocabulario de la
// lista, para calcular la distancia una vez por palabra distinta y no una vez
// por cada vez que aparece.
export const palabrasDeCampos = (campos) => new Set((campos || []).flatMap(palabrasDe));

// Distancia de edición entre `t` y el PRINCIPIO de `w` que mejor le queda
// (Damerau, alineación óptima: cambiar, sobrar, faltar o voltear dos letras
// contiguas). Pasado `max` deja de contar y devuelve max + 1.
export function distanciaAlInicio(t, w, max) {
  const n = t.length;
  const m = Math.min(w.length, n + max);
  if (m < n - max) return max + 1;
  let ante = new Array(m + 1).fill(0);   // fila i - 2
  let prev = new Array(m + 1).fill(0);   // fila i - 1
  let fila = new Array(m + 1).fill(0);   // fila i
  for (let j = 0; j <= m; j++) prev[j] = j;
  for (let i = 1; i <= n; i++) {
    fila[0] = i;
    let menor = i;
    for (let j = 1; j <= m; j++) {
      let v = Math.min(prev[j] + 1, fila[j - 1] + 1, prev[j - 1] + (t[i - 1] === w[j - 1] ? 0 : 1));
      if (i > 1 && j > 1 && t[i - 1] === w[j - 2] && t[i - 2] === w[j - 1]) v = Math.min(v, ante[j - 2] + 1);
      fila[j] = v;
      if (v < menor) menor = v;
    }
    if (menor > max) return max + 1;
    [ante, prev, fila] = [prev, fila, ante];
  }
  let mejor = max + 1;
  for (let j = Math.max(0, n - max); j <= m; j++) if (prev[j] < mejor) mejor = prev[j];
  return mejor;
}

// Para cada palabra de la consulta que admite holgura, las palabras del
// vocabulario escritas casi igual. null si ninguna se puede proponer.
export function cercanasDe(consulta, vocabulario) {
  if (!consulta || consulta.vacia || consulta.exacta) return null;
  const porTermino = new Map();
  consulta.terminos.forEach((t) => {
    const k = letrasDeHolgura(t);
    if (!k || porTermino.has(t)) return;
    const cerca = new Set();
    for (const w of vocabulario) {
      if (w.length < t.length - k || CON_DIGITO.test(w)) continue;
      if (distanciaAlInicio(t, w, k) <= k) cerca.add(w);
    }
    if (cerca.size) porTermino.set(t, cerca);
  });
  return porTermino.size ? porTermino : null;
}

// Para un registro, la palabra escrita casi igual de cada término que no está
// tal cual; null si a alguno no le alcanza. `palabras`: el Set del registro.
export function casiIgualEn(consulta, campos, cercanas, palabras = palabrasDeCampos(campos)) {
  if (!cercanas) return null;
  const todo = campos.join('\n');
  const usadas = new Map();
  for (const t of consulta.terminos) {
    if (todo.includes(t)) continue;
    const cerca = cercanas.get(t);
    let hallada = null;
    if (cerca) for (const w of cerca) if (palabras.has(w)) { hallada = w; break; }
    if (!hallada) return null;
    usadas.set(t, hallada);
  }
  return usadas.size ? usadas : null;
}

// Los niveles de una lista entera: el estricto de cada registro y, si NINGUNO
// coincide, lo escrito casi igual (NIVEL_CASI_IGUAL). `camposDeLista`: un
// arreglo por registro con sus campos YA plegados. `casiIgual: false` lo apaga
// (quien junta varias listas lo decide entre todas). Devuelve un arreglo de
// niveles (null = no coincide).
export function nivelesDe(consulta, camposDeLista, { casiIgual = true } = {}) {
  const niveles = camposDeLista.map((campos) => nivelDe(consulta, campos));
  if (!casiIgual || !consulta || consulta.vacia || consulta.exacta || niveles.some((n) => n != null)) return niveles;
  if (!consulta.terminos.some(letrasDeHolgura)) return niveles;
  const palabras = camposDeLista.map(palabrasDeCampos);
  const vocabulario = new Set();
  palabras.forEach((s) => s.forEach((w) => vocabulario.add(w)));
  const cercanas = cercanasDe(consulta, vocabulario);
  if (!cercanas) return niveles;
  return camposDeLista.map((campos, i) => (casiIgualEn(consulta, campos, cercanas, palabras[i]) ? NIVEL_CASI_IGUAL : null));
}

// ── Las palabras de la búsqueda, iguales en toda la app ────────────────────
export function textosBusqueda(texto) {
  const q = String(texto || '').trim();
  return {
    exactas: `Coinciden con «${q}»`,
    juntas: 'Con todas las palabras',
    parecidos: 'Parecidos',
    nada: `Nada coincide con «${q}»`,
  };
}

// Ordena por nivel (lo que coincide primero) sin perder el orden de la lista
// dentro de cada nivel. `camposDe(item)` devuelve los textos del registro.
// Cada resultado lleva `_nivel`. Sin consulta devuelve la lista tal cual. Si
// nada coincide, trae lo escrito casi igual (nivelesDe); `casiIgual: false`
// lo apaga para quien junta varias listas y lo decide entre todas.
export function buscarEn(items, q, camposDe, { casiIgual = true } = {}) {
  const consulta = consultaDe(q);
  if (consulta.vacia) return items;
  const niveles = nivelesDe(consulta, items.map((it) => (camposDe(it) || []).map(plegar)), { casiIgual });
  const res = [];
  items.forEach((it, orden) => {
    const n = niveles[orden];
    if (n != null) res.push({ it, n, orden });
  });
  res.sort((a, b) => a.n - b.n || a.orden - b.orden);
  return res.map(({ it, n }) => (it && typeof it === 'object' ? { ...it, _nivel: n } : it));
}

// Los grupos de una lista mientras se busca, con las palabras de siempre:
//  · todo coincide con la frase: una lista lisa, sin rótulos;
//  · mezcla de niveles: un grupo por nivel, rotulado;
//  · nada dice la frase: primero "Nada coincide con «X»" y luego lo que hay.
// Lo escrito casi igual (nivel 3) va con los Parecidos: solo aparece cuando
// nada coincidió, así que la lista dice "Nada coincide con «X»" y debajo
// "Parecidos". `items` ya pasados por buscarEn (llevan `_nivel`). Forma de
// grupo de la carpeta: { clave, etiqueta, items, sinConteo }.
export function gruposDeBusqueda(items, q, nivelDeItem = (it) => it._nivel ?? 0) {
  const consulta = consultaDe(q);
  const t = textosBusqueda(consulta.texto);
  const por = [[], [], []];
  items.forEach((it) => { por[Math.min(2, Math.max(0, nivelDeItem(it)))].push(it); });
  const [cero, uno, dos] = por;
  if (!uno.length && !dos.length) return [{ clave: 'buscar-0', etiqueta: null, items: cero }];
  const grupos = [];
  if (cero.length) grupos.push({ clave: 'buscar-0', etiqueta: t.exactas, items: cero });
  else grupos.push({ clave: 'buscar-nada', etiqueta: t.nada, items: [], sinConteo: true });
  if (uno.length) grupos.push({ clave: 'buscar-1', etiqueta: t.juntas, items: uno });
  if (dos.length) grupos.push({ clave: 'buscar-2', etiqueta: t.parecidos, items: dos });
  return grupos;
}
